/**
 * Upload Authorization Form Component
 *
 * Option B: Caregiver uploads a signed paper authorization form.
 * Flow:
 * 1. Caregiver downloads blank authorization form template (PDF)
 * 2. Patient signs the physical document
 * 3. Caregiver scans/photographs the signed document
 * 4. Caregiver uploads the file and enters patient info + attestation
 * 5. Calls uploadPatientAuthorizationHttp to unlock the CaseFile
 */

import { useState, useRef } from 'react';
import { auth } from '../../lib/firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { buildHttpHeaders } from '../../utils/appCheckToken';
import { getCloudFunctionsBaseUrl } from '../../utils/cloudFunctionUrl';
import {
  Upload,
  Download,
  FileText,
  User,
  Calendar,
  AlertTriangle,
  Loader2,
  X,
  File,
  Shield,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface UploadAuthorizationFormProps {
  caseFileId: string;
  caseFileName: string;
  careRecipientName?: string;
  careRecipientDob?: string;
  careRecipientEmail?: string;
  onComplete: () => void;
  onCancel: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/tiff'];
const ALLOWED_EXTENSIONS = '.pdf, .jpg, .jpeg, .png, .tif, .tiff';

const ATTESTATION_TEXT =
  'I attest that the uploaded document is a true and accurate copy of the signed ' +
  'Patient Authorization Form. I confirm that the patient (or their legal representative) ' +
  'signed this document voluntarily, and the signature date entered above is correct. ' +
  'I understand that falsifying this attestation may result in account termination and ' +
  'potential legal consequences.';


/**
 * Generate SHA-256 hash for file integrity verification
 */
async function hashFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ============================================================================
// Main Component
// ============================================================================

export function UploadAuthorizationForm({
  caseFileId,
  careRecipientName,
  careRecipientDob,
  careRecipientEmail,
  onComplete,
  onCancel,
}: UploadAuthorizationFormProps): JSX.Element {
  // Patient info state
  const [patientName, setPatientName] = useState(careRecipientName || '');
  const [patientDob, setPatientDob] = useState(careRecipientDob || '');
  const [patientEmail, setPatientEmail] = useState(careRecipientEmail || '');
  const [signatureDate, setSignatureDate] = useState('');

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Attestation
  const [attestationChecked, setAttestationChecked] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation
  const isPatientInfoValid =
    patientName.trim().length > 0 && patientDob.length > 0 && signatureDate.length > 0;
  const isFileSelected = selectedFile !== null;
  const canSubmit = isPatientInfoValid && isFileSelected && attestationChecked && !isSubmitting;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`Invalid file type. Accepted formats: ${ALLOWED_EXTENSIONS}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!canSubmit || !selectedFile) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();
      const userId = user.uid;

      // Step 1: Upload file to Cloud Storage
      setIsUploading(true);
      const timestamp = Date.now();
      const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `temp/${userId}/${caseFileId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, storagePath);

      // Compute file hash for integrity
      const fileHash = await hashFile(selectedFile);

      // Upload with progress tracking
      await new Promise<void>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, selectedFile, {
          contentType: selectedFile.type,
          customMetadata: { userId },
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setUploadProgress(progress);
          },
          (uploadError) => {
            reject(uploadError);
          },
          () => {
            resolve();
          }
        );
      });

      setIsUploading(false);

      // Step 2: Call uploadPatientAuthorizationHttp with file metadata
      const baseUrl = getCloudFunctionsBaseUrl();
      const headers = await buildHttpHeaders(token);
      const response = await fetch(`${baseUrl}/uploadPatientAuthorizationHttp`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          caseFileId,
          storagePath,
          fileName: selectedFile.name,
          mimeType: selectedFile.type,
          fileHash,
          patient: {
            fullName: patientName.trim(),
            dateOfBirth: patientDob,
            ...(patientEmail.trim() && { email: patientEmail.trim() }),
          },
          signatureDateAttested: signatureDate,
          attestationText: ATTESTATION_TEXT,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        if (response.status === 429) {
          const retryAfter = errorData.retryAfterSeconds || 'a few';
          throw new Error(`Please wait ${retryAfter} seconds before trying again.`);
        }

        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        onComplete();
      } else {
        throw new Error(result.error || 'Authorization upload failed');
      }
    } catch (err) {
      console.error('Error uploading authorization:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload authorization');
      setIsUploading(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Upload className="w-6 h-6 text-evergreen" />
        </div>
        <h2 className="text-xl font-display text-deep-forest mb-1">
          Upload Signed Authorization
        </h2>
        <p className="text-sm text-muted">
          Upload the patient&apos;s signed authorization form to unlock this CaseFile
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Step 1: Download Blank Form */}
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="font-semibold text-ink flex items-center gap-2">
            <Download className="w-5 h-5 text-evergreen" />
            Step 1: Download Blank Form
          </h3>
          <p className="text-sm text-muted mt-1">
            Print and have the patient sign the authorization form
          </p>
        </div>
        <div className="card-body">
          <a
            href={`${getCloudFunctionsBaseUrl()}/getBlankAuthorizationTemplateHttp`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Download Authorization Form (PDF)
          </a>
          <p className="text-xs text-muted mt-2">
            This form contains all HIPAA-required elements for patient authorization.
          </p>
        </div>
      </div>

      {/* Step 2: Patient Information */}
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="font-semibold text-ink flex items-center gap-2">
            <User className="w-5 h-5 text-evergreen" />
            Step 2: Patient Information
          </h3>
          <p className="text-sm text-muted mt-1">
            Enter the patient details as they appear on the signed form
          </p>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label htmlFor="uploadPatientName" className="label">
              Patient Full Legal Name <span className="text-red-500">*</span>
            </label>
            <input
              id="uploadPatientName"
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              className="input"
              placeholder="Enter patient's full legal name"
              disabled={isSubmitting}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="uploadPatientDob" className="label">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                id="uploadPatientDob"
                type="date"
                value={patientDob}
                onChange={(e) => setPatientDob(e.target.value)}
                className="input"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="uploadSignatureDate" className="label">
                Date on Signature <span className="text-red-500">*</span>
              </label>
              <input
                id="uploadSignatureDate"
                type="date"
                value={signatureDate}
                onChange={(e) => setSignatureDate(e.target.value)}
                className="input"
                max={new Date().toISOString().split('T')[0]}
                disabled={isSubmitting}
              />
              <p className="helper-text">The date the patient signed the document</p>
            </div>
          </div>
          <div>
            <label htmlFor="uploadPatientEmail" className="label">
              Patient Email <span className="text-muted">(optional)</span>
            </label>
            <input
              id="uploadPatientEmail"
              type="email"
              value={patientEmail}
              onChange={(e) => setPatientEmail(e.target.value)}
              className="input"
              placeholder="patient@example.com"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Step 3: Upload Signed Document */}
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="font-semibold text-ink flex items-center gap-2">
            <Upload className="w-5 h-5 text-evergreen" />
            Step 3: Upload Signed Document
          </h3>
          <p className="text-sm text-muted mt-1">
            Upload a scan or photo of the signed authorization form
          </p>
        </div>
        <div className="card-body">
          {!selectedFile ? (
            <div
              className="border-2 border-dashed border-soft-neutral rounded-lg p-8 text-center hover:border-evergreen/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-muted mx-auto mb-3" />
              <p className="text-sm font-medium text-ink mb-1">
                Click to select a file
              </p>
              <p className="text-xs text-muted">
                PDF, JPG, or PNG &middot; Max 10MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Upload signed authorization document (PDF, JPG, or PNG)"
                accept={ALLOWED_EXTENSIONS}
                onChange={handleFileSelect}
                className="hidden"
                disabled={isSubmitting}
              />
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-airy rounded-lg p-4">
              <div className="w-10 h-10 bg-evergreen/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <File className="w-5 h-5 text-evergreen" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{selectedFile.name}</p>
                <p className="text-xs text-muted">{formatFileSize(selectedFile.size)}</p>
              </div>
              {isUploading ? (
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-soft-neutral rounded-full overflow-hidden">
                    <div
                      className="h-full bg-evergreen rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted">{uploadProgress}%</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  disabled={isSubmitting}
                  className="btn-ghost btn-sm text-muted hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step 4: Attestation */}
      <div className="card mb-6">
        <div className="card-header">
          <h3 className="font-semibold text-ink flex items-center gap-2">
            <Calendar className="w-5 h-5 text-evergreen" />
            Step 4: Caregiver Attestation
          </h3>
        </div>
        <div className="card-body">
          <label className="flex items-start gap-3 bg-airy rounded-lg p-4 cursor-pointer">
            <input
              type="checkbox"
              checked={attestationChecked}
              onChange={(e) => setAttestationChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
              disabled={isSubmitting}
            />
            <span className="text-sm text-ink">{ATTESTATION_TEXT}</span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="btn-secondary"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="btn-primary flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isUploading ? `Uploading (${uploadProgress}%)...` : 'Processing...'}
            </>
          ) : (
            <>
              <Shield className="w-4 h-4" />
              Upload & Unlock CaseFile
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default UploadAuthorizationForm;
